import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Store, Plus, ShieldCheck, Trash2, CheckCircle, XCircle, Mail, Phone, Globe, ChevronDown, ChevronRight, Edit, Ban, Image, Video } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const AdminExhibitorsTab = ({ exhibitors, fetchDashboardData, verifyUser, openCancelModal, onDeleteUser }) => {
  const { t } = useTranslation();
  const [expandedRows, setExpandedRows] = useState({});

  // Booth Edit State
  const [showBoothEditModal, setShowBoothEditModal] = useState(false);
  const [editingBooth, setEditingBooth] = useState(null);
  const [boothProducts, setBoothProducts] = useState([]);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', description: '', price: 0, image_url: '' });
  const [newProductImage, setNewProductImage] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderCompanyInfo = (user) => {
    const ci = user.company_info;
    if (!ci) return <span className="text-slate-400">No company info</span>;
    return (
      <div className="text-sm space-y-1">
        <p><strong>{ci.company_name}</strong></p>
        <p className="text-slate-600">{ci.street_address}, {ci.city}, {ci.state_province} {ci.postal_code}</p>
        <p className="text-slate-600">{ci.country}</p>
        <p className="text-slate-600"><Phone className="inline h-3 w-3 mr-1" />{ci.phone}</p>
        <p className="text-slate-600"><Mail className="inline h-3 w-3 mr-1" />{ci.company_email}</p>
        {ci.website && <p className="text-blue-600"><Globe className="inline h-3 w-3 mr-1" />{ci.website}</p>}
        <p className="text-slate-500 text-xs mt-1">Industry: {ci.industry}</p>
        <p className="text-slate-500 text-xs">{ci.description}</p>
      </div>
    );
  };

  const openBoothEditModal = async (booth) => {
    setEditingBooth(booth);
    setShowBoothEditModal(true);
    try {
      const res = await axios.get(`${API}/products/booth/${booth.id}`);
      setBoothProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setBoothProducts([]);
    }
  };

  const uploadBoothMedia = async (file, mediaType) => {
    if (!editingBooth || !file) return;
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', mediaType);
      const res = await axios.post(`${API}/upload/booth/${editingBooth.id}?media_type=${mediaType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${mediaType} uploaded successfully!`);
      if (mediaType === 'logo') {
        setEditingBooth({ ...editingBooth, logo_url: res.data.url });
      } else if (mediaType === 'banner') {
        setEditingBooth({ ...editingBooth, banner_url: res.data.url });
      } else {
        const newMedia = { id: Date.now().toString(), url: res.data.url, type: res.data.type, filename: file.name };
        setEditingBooth({ ...editingBooth, media: [...(editingBooth.media || []), newMedia] });
      }
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload media');
    } finally {
      setUploadingMedia(false);
    }
  };

  const deleteBoothMedia = async (mediaId) => {
    if (!editingBooth) return;
    try {
      await axios.delete(`${API}/upload/booth/${editingBooth.id}/media/${mediaId}`);
      toast.success('Media deleted successfully!');
      setEditingBooth({
        ...editingBooth,
        media: editingBooth.media?.filter(m => m.id !== mediaId) || []
      });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete media');
    }
  };

  const addProduct = async () => {
    if (!editingBooth) return;
    setUploadingMedia(true);
    try {
      const productData = { ...newProduct, booth_id: editingBooth.id };
      const res = await axios.post(`${API}/products`, productData);
      let finalProduct = res.data;
      if (newProductImage) {
        try {
          const formData = new FormData();
          formData.append('file', newProductImage);
          const imgRes = await axios.post(`${API}/upload/product/${res.data.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          finalProduct = { ...finalProduct, image_url: imgRes.data.url };
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
          toast.warning('Product created but image upload failed');
        }
      }
      toast.success('Product added successfully!');
      setBoothProducts([...boothProducts, finalProduct]);
      setNewProduct({ name: '', description: '', price: 0, image_url: '' });
      setNewProductImage(null);
      setShowAddProductModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setUploadingMedia(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted successfully!');
      setBoothProducts(boothProducts.filter(p => p.id !== productId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const uploadProductImage = async (productId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/product/${productId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Product image uploaded!');
      setBoothProducts(boothProducts.map(p => 
        p.id === productId ? { ...p, image_url: res.data.url } : p
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload product image');
    }
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Exhibitors & Representatives</h2>
        {exhibitors.length === 0 ? (
          <div className="text-center py-8">
            <Store className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No exhibitors yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>{t('dashboard.name')} / {t('dashboard.email')}</TableHead>
                  <TableHead>{t('dashboard.company')}</TableHead>
                  <TableHead>{t('dashboard.total_booths')} / {t('dashboard.events')}</TableHead>
                  <TableHead>{t('dashboard.reps')}</TableHead>
                  <TableHead>{t('dashboard.status')}</TableHead>
                  <TableHead>{t('dashboard.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {exhibitors.map((exhibitor) => (
                  <React.Fragment key={exhibitor.id}>
                    <TableRow className="hover:bg-slate-50">
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => toggleRowExpand(exhibitor.id)}>
                          {expandedRows[exhibitor.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{exhibitor.name}</p>
                          <p className="text-sm text-slate-500">{exhibitor.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">{renderCompanyInfo(exhibitor)}</TableCell>
                      <TableCell>
                        {exhibitor.booths?.length > 0 ? (
                          <div className="space-y-1">
                            {exhibitor.booths.map((booth, idx) => (
                              <div key={idx} className="text-sm flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${booth.tier === 'elite' ? 'bg-purple-100 text-purple-700' : booth.tier === 'premium' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-700'}`}>
                                  {booth.tier?.toUpperCase()}
                                </span>
                                <span className="truncate max-w-[120px]">{booth.event?.name || 'No event'}</span>
                                <Button size="sm" variant="ghost" className="h-6 px-1 text-blue-600 hover:text-blue-700"
                                  onClick={() => openBoothEditModal(booth)} title="Edit Booth Media & Products"
                                  data-testid={`edit-booth-${booth.id}`}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 px-1 text-red-500 hover:text-red-700"
                                  onClick={() => openCancelModal(exhibitor, booth.event_id)}
                                  title={`Remove from ${booth.event?.name || 'event'}`}
                                  data-testid={`delete-booth-${booth.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-slate-400">No booths</span>}
                      </TableCell>
                      <TableCell>
                        {exhibitor.reps?.length > 0 ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{exhibitor.reps.length} reps</span>
                        ) : <span className="text-slate-400">No reps</span>}
                      </TableCell>
                      <TableCell>
                        {exhibitor.is_verified ? (
                          <span className="flex items-center text-green-600 text-sm"><ShieldCheck className="h-4 w-4 mr-1" />Verified</span>
                        ) : (
                          <span className="text-amber-600 text-sm">Pending</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!exhibitor.is_verified && (
                            <Button size="sm" variant="outline" className="text-green-600" onClick={() => verifyUser(exhibitor.id)} title="Verify">
                              <CheckCircle className="h-3 w-3" />
                            </Button>
                          )}
                          {exhibitor.booths?.length > 0 && (
                            <Button size="sm" variant="outline" className="text-amber-600" 
                              onClick={() => openCancelModal(exhibitor, exhibitor.booths[0]?.event_id)} title="Cancel Registration">
                              <Ban className="h-3 w-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="destructive" onClick={() => onDeleteUser(exhibitor)} title="Delete User">
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedRows[exhibitor.id] && exhibitor.reps?.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50 p-4">
                          <h4 className="font-medium text-sm mb-2">Representatives:</h4>
                          <div className="grid grid-cols-3 gap-2">
                            {exhibitor.reps.map((rep, idx) => (
                              <div key={idx} className="bg-white p-2 rounded border text-sm">
                                <p className="font-medium">{rep.name}</p>
                                <p className="text-slate-500">{rep.email}</p>
                                <p className="text-xs text-slate-400">{rep.is_password_set ? 'Active' : 'Pending Setup'}</p>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Booth Edit Modal */}
      <Dialog open={showBoothEditModal} onOpenChange={(open) => { setShowBoothEditModal(open); if (!open) { setEditingBooth(null); setBoothProducts([]); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-primary" />
              Edit Booth: {editingBooth?.company_name}
            </DialogTitle>
            <DialogDescription>Upload or modify booth media (logo, banner, gallery) and manage products</DialogDescription>
          </DialogHeader>

          {editingBooth && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Booth Logo</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    {editingBooth.logo_url ? (
                      <div className="relative">
                        <img src={editingBooth.logo_url.startsWith('/api') ? `${API}${editingBooth.logo_url.replace('/api', '')}` : editingBooth.logo_url} 
                          alt="Logo" className="max-h-32 max-w-full object-contain mx-auto rounded" />
                        <p className="text-xs text-slate-500 mt-2">Current Logo</p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Image className="h-8 w-8 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-500 mt-2">No logo uploaded</p>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadBoothMedia(e.target.files[0], 'logo')}
                      className="hidden" id="logo-upload" />
                    <Button type="button" variant="outline" size="sm" className="mt-2" disabled={uploadingMedia}
                      onClick={() => document.getElementById('logo-upload').click()}>
                      {uploadingMedia ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Booth Banner</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    {editingBooth.banner_url ? (
                      <div className="relative">
                        <img src={editingBooth.banner_url.startsWith('/api') ? `${API}${editingBooth.banner_url.replace('/api', '')}` : editingBooth.banner_url}
                          alt="Banner" className="h-24 w-full object-cover rounded" />
                        <p className="text-xs text-slate-500 mt-2">Current Banner</p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Image className="h-8 w-8 mx-auto text-slate-400" />
                        <p className="text-sm text-slate-500 mt-2">No banner uploaded</p>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadBoothMedia(e.target.files[0], 'banner')}
                      className="hidden" id="banner-upload" />
                    <Button type="button" variant="outline" size="sm" className="mt-2" disabled={uploadingMedia}
                      onClick={() => document.getElementById('banner-upload').click()}>
                      {uploadingMedia ? 'Uploading...' : 'Upload Banner'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-medium">Gallery (Images, Videos, PDFs)</Label>
                  <div>
                    <input type="file" accept="image/*,video/*,.pdf"
                      onChange={(e) => e.target.files?.[0] && uploadBoothMedia(e.target.files[0], 'gallery')}
                      className="hidden" id="gallery-upload" />
                    <Button type="button" variant="outline" size="sm" disabled={uploadingMedia}
                      onClick={() => document.getElementById('gallery-upload').click()}>
                      <Plus className="h-4 w-4 mr-1" /> Add Media
                    </Button>
                  </div>
                </div>
                <div className="border rounded-lg p-4 bg-slate-50">
                  {editingBooth.media?.length > 0 ? (
                    <div className="grid grid-cols-4 gap-3">
                      {editingBooth.media.map((item) => (
                        <div key={item.id} className="relative group">
                          {item.type === 'image' ? (
                            <img src={item.url.startsWith('/api') ? `${API}${item.url.replace('/api', '')}` : item.url}
                              alt={item.filename} className="h-20 w-full object-cover rounded" />
                          ) : item.type === 'video' ? (
                            <div className="h-20 w-full bg-slate-200 rounded flex items-center justify-center">
                              <Video className="h-8 w-8 text-slate-500" />
                            </div>
                          ) : (
                            <div className="h-20 w-full bg-red-50 rounded flex items-center justify-center">
                              <span className="text-xs text-red-600">PDF</span>
                            </div>
                          )}
                          <button onClick={() => deleteBoothMedia(item.id)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <XCircle className="h-4 w-4" />
                          </button>
                          <p className="text-xs text-slate-500 truncate mt-1">{item.filename}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No gallery media uploaded</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-medium">Products ({boothProducts.length})</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAddProductModal(true)}>
                    <Plus className="h-4 w-4 mr-1" /> Add Product
                  </Button>
                </div>
                <div className="border rounded-lg p-4 bg-slate-50">
                  {boothProducts.length > 0 ? (
                    <div className="space-y-3">
                      {boothProducts.map((product) => (
                        <div key={product.id} className="flex items-center gap-4 bg-white p-3 rounded border">
                          <div className="w-16 h-16 flex-shrink-0">
                            {product.image_url ? (
                              <img src={product.image_url.startsWith('/api') ? `${API}${product.image_url.replace('/api', '')}` : product.image_url}
                                alt={product.name} className="w-full h-full object-cover rounded" />
                            ) : (
                              <div className="w-full h-full bg-slate-100 rounded flex items-center justify-center">
                                <Image className="h-6 w-6 text-slate-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{product.name}</p>
                            <p className="text-sm text-slate-500 truncate">{product.description}</p>
                            <p className="text-sm font-semibold text-primary">${product.price}</p>
                          </div>
                          <div className="flex gap-2">
                            <input type="file" accept="image/*"
                              onChange={(e) => e.target.files?.[0] && uploadProductImage(product.id, e.target.files[0])}
                              className="hidden" id={`product-img-${product.id}`} />
                            <Button type="button" variant="ghost" size="sm"
                              onClick={() => document.getElementById(`product-img-${product.id}`).click()}>
                              <Image className="h-4 w-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" className="text-red-500"
                              onClick={() => deleteProduct(product.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No products added</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoothEditModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Modal */}
      <Dialog open={showAddProductModal} onOpenChange={(open) => { setShowAddProductModal(open); if (!open) { setNewProductImage(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Product</DialogTitle>
            <DialogDescription>Add a product to {editingBooth?.company_name}'s booth</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Product Name *</Label>
              <Input value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder="Enter product name" required />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newProduct.description} onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder="Product description" rows={3} />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input type="number" min="0" step="0.01" value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                {newProductImage ? (
                  <div className="flex items-center gap-4">
                    <img src={URL.createObjectURL(newProductImage)} alt="Preview" className="h-20 w-20 object-cover rounded" />
                    <div className="flex-1">
                      <p className="text-sm font-medium truncate">{newProductImage.name}</p>
                      <p className="text-xs text-slate-500">{(newProductImage.size / 1024).toFixed(1)} KB</p>
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 mt-1 h-6 px-2"
                        onClick={() => setNewProductImage(null)}>Remove</Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <Image className="h-8 w-8 mx-auto text-slate-400" />
                    <p className="text-sm text-slate-500 mt-2">Click to select an image</p>
                    <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && setNewProductImage(e.target.files[0])}
                      className="hidden" id="new-product-image" />
                    <Button type="button" variant="outline" size="sm" className="mt-2"
                      onClick={() => document.getElementById('new-product-image').click()}>Select Image</Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddProductModal(false); setNewProductImage(null); }}>Cancel</Button>
            <Button onClick={addProduct} disabled={!newProduct.name || uploadingMedia}>
              {uploadingMedia ? 'Adding...' : 'Add Product'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminExhibitorsTab;
